import React from "react";
import { arrayOf, shape } from "prop-types";
import get from "lodash/get";
import { differenceInHours, differenceInMinutes, format } from "date-fns";
import { kn } from "date-fns/locale";
import { Link, ResponsiveHeroImage } from "@quintype/components";
import CardImage from "../../../atoms/cardimage";
import VideoIcon from "../../../atoms/icons/video-icon";

import { getHeadline } from "../../../helper/utils";

import PremiumIcon from "../../../atoms/other-icons/PremiumIcon";
import ClockIcon from "../../../atoms/other-icons/ClockIcon";
import EyeIcon from "../../../atoms/other-icons/EyeIcon";
import LiveIcon from "../../../atoms/icons/live-icon";
import "./premium-story-block.m.css";

const getPublishTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const hours = differenceInHours(now, date);
  if (hours < 24) {
    if (hours === 0) {
      const mins = differenceInMinutes(now, date);
      if (mins === 0) return "Just now";
      return (
        <span>
          <span styleName="numeric-font">{mins}</span>
          <span styleName="desktop-meta-text">{" "} minute ago</span>
          <span styleName="mobile-meta-text">{" "}min. ago</span>
        </span>
      );
    }
    return (
      <span>
        <span styleName="numeric-font">{hours}</span>
        <span styleName="desktop-meta-text">{" "} hour ago</span>
        <span styleName="mobile-meta-text">{" "}hr. ago</span>
      </span>
    );
  }
  return format(date, "d MMMM yyyy");
};

const getReadTime = (story) => {
  const readTime = get(story, ["read-time"], null);
  if (readTime) {
    return (
      <span>
        <span styleName="numeric-font">{readTime}</span>
        <span styleName="desktop-meta-text">{" "}min read</span>
        <span styleName="mobile-meta-text">{" "}min read</span>
      </span>
    );
  }
  return "";
};

const getHeroImageSlug = (story) => {
  return (
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]) ||
    get(story, "hero-image-s3-key", "")
  );
};

const MobileGridClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" styleName="mobile-grid-clock-icon">
    <mask id="mask0_2799_9232" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="12" height="12">
      <rect width="12" height="12" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#mask0_2799_9232)">
      <path d="M7.56385 8.34273L8.26167 7.64491L6.41744 5.80067V3.50784H5.42055V6.19943L7.56385 8.34273ZM5.91899 10.9845C5.22948 10.9845 4.58151 10.8536 3.97507 10.5919C3.36863 10.3303 2.84111 9.97513 2.39251 9.52653C1.94392 9.07793 1.58878 8.55041 1.32709 7.94397C1.06541 7.33754 0.93457 6.68956 0.93457 6.00005C0.93457 5.31054 1.06541 4.66256 1.32709 4.05612C1.58878 3.44969 1.94392 2.92217 2.39251 2.47357C2.84111 2.02497 3.36863 1.66983 3.97507 1.40815C4.58151 1.14647 5.22948 1.01562 5.91899 1.01562C6.60851 1.01562 7.25648 1.14647 7.86292 1.40815C8.46936 1.66983 8.99688 2.02497 9.44547 2.47357C9.89407 2.92217 10.2492 3.44969 10.5109 4.05612C10.7726 4.66256 10.9034 5.31054 10.9034 6.00005C10.9034 6.68956 10.7726 7.33754 10.5109 7.94397C10.2492 8.55041 9.89407 9.07793 9.44547 9.52653C8.99688 9.97513 8.46936 10.3303 7.86292 10.5919C7.25648 10.8536 6.60851 10.9845 5.91899 10.9845ZM5.91899 9.98759C7.02387 9.98759 7.96468 9.59922 8.74142 8.82248C9.51816 8.04574 9.90653 7.10493 9.90653 6.00005C9.90653 4.89517 9.51816 3.95436 8.74142 3.17762C7.96468 2.40088 7.02387 2.01251 5.91899 2.01251C4.81411 2.01251 3.8733 2.40088 3.09656 3.17762C2.31982 3.95436 1.93146 4.89517 1.93146 6.00005C1.93146 7.10493 2.31982 8.04574 3.09656 8.82248C3.8733 9.59922 4.81411 9.98759 5.91899 9.98759Z" fill="#999999"/>
    </g>
  </svg>
);



const MobileGridEyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="11"
    height="8"
    viewBox="0 0 11 8"
    fill="none"
    styleName="mobile-grid-eye-icon"
  >
    <path d="M7.07165 5.3271C7.50779 4.89097 7.72586 4.36137 7.72586 3.73832C7.72586 3.11526 7.50779 2.58567 7.07165 2.14953C6.63551 1.7134 6.10592 1.49533 5.48287 1.49533C4.85981 1.49533 4.33022 1.7134 3.89408 2.14953C3.45794 2.58567 3.23988 3.11526 3.23988 3.73832C3.23988 4.36137 3.45794 4.89097 3.89408 5.3271C4.33022 5.76324 4.85981 5.98131 5.48287 5.98131C6.10592 5.98131 6.63551 5.76324 7.07165 5.3271ZM4.5296 4.69159C4.26791 4.42991 4.13707 4.11215 4.13707 3.73832C4.13707 3.36449 4.26791 3.04673 4.5296 2.78505C4.79128 2.52336 5.10903 2.39252 5.48287 2.39252C5.8567 2.39252 6.17445 2.52336 6.43614 2.78505C6.69782 3.04673 6.82866 3.36449 6.82866 3.73832C6.82866 4.11215 6.69782 4.42991 6.43614 4.69159C6.17445 4.95327 5.8567 5.08411 5.48287 5.08411C5.10903 5.08411 4.79128 4.95327 4.5296 4.69159ZM2.16822 6.46106C1.17134 5.78401 0.448598 4.87643 0 3.73832C0.448598 2.60021 1.17134 1.69263 2.16822 1.01558C3.16511 0.338525 4.26999 0 5.48287 0C6.69574 0 7.80062 0.338525 8.79751 1.01558C9.79439 1.69263 10.5171 2.60021 10.9657 3.73832C10.5171 4.87643 9.79439 5.78401 8.79751 6.46106C7.80062 7.13811 6.69574 7.47663 5.48287 7.47663C4.26999 7.47663 3.16511 7.13811 2.16822 6.46106ZM8.06854 5.73832C8.85358 5.24403 9.45379 4.57736 9.86916 3.73832C9.45379 2.89927 8.85358 2.23261 8.06854 1.73832C7.28349 1.24403 6.4216 0.996885 5.48287 0.996885C4.54413 0.996885 3.68224 1.24403 2.8972 1.73832C2.11215 2.23261 1.51194 2.89927 1.09657 3.73832C1.51194 4.57736 2.11215 5.24403 2.8972 5.73832C3.68224 6.23261 4.54413 6.47975 5.48287 6.47975C6.4216 6.47975 7.28349 6.23261 8.06854 5.73832Z" fill="#999999"/>
  </svg>
);

const PremiumStoryBlock = ({ stories = [] }) => {
  if (!stories || stories.length === 0) return null;

  const heroStory = stories[0];
  const gridStories = stories.slice(1, 5);



  const getObjectPosition = (metadata) => {
    const focusPoint = get(metadata, ["focus-point"]);
    const width = get(metadata, ["width"]);
    const height = get(metadata, ["height"]);
    if (focusPoint && focusPoint.length === 2 && width && height) {
      return `${(focusPoint[0] / width) * 100}% ${(focusPoint[1] / height) * 100}%`;
    }
    return "50% 50%";
  };

  const renderGridRow = (rowStories) => (
    <div styleName="story-row">
      {rowStories.map((story, index) => {
        const heroImageSlug = getHeroImageSlug(story);



        return (
          <div key={story.id || index} styleName="grid-story">
            <Link href={`/${story.slug}`} styleName="story-link">
              <div styleName="image-relative-container" style={{ "--object-position": getObjectPosition(get(story, "hero-image-metadata", {})) }}>
                {heroImageSlug ? (
                  <ResponsiveHeroImage
                    story={story}
                    slug={decodeURIComponent(heroImageSlug)}
                    metadata={get(story, "hero-image-metadata", {})}
                    aspectRatio={[16, 9]}
                    defaultWidth={437}
                    widths={[218, 437]}
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    type="image/webp"
                    imgParams={{ auto: ["format", "compress"], fit: "max", q: 70 }}
                    styleName={`grid-img ${get(story, ["hero-image-metadata", "focus-point"]) ? "img-cover" : "img-contain"}`}
                    alt={getHeadline(story) || "Story image"}
                  />
                ) : (
                  <div styleName="grid-img">
                    <CardImage story={{}} imageType="image16x9" />
                  </div>
                )}
                {story["story-template"] === "photo" && (
                  <div styleName="camera-icon-wrapper">
                    <img src="/icons/imageIcon.png" alt="err" />
                  </div>
                )}
                {story["story-template"] === "video" && (
                  <div styleName="video-icon-wrapper">
                    <VideoIcon />
                  </div>
                )}
              </div>
            </Link>
            <div styleName="grid-text">
              <div>
                <Link href={`/${story.slug}`}>
                  <h4 styleName="grid-headline">
                    <span styleName="premium-icon-wrapper">
                      <PremiumIcon />
                    </span>
                    {story["story-template"] === "live-blog" && (
                      <span styleName="red-dot-wrapper">
                        <LiveIcon />
                      </span>
                    )}
                    {" "}{getHeadline(story) || ""}
                  </h4>
                </Link>
                <p styleName="grid-section">
                  <Link href={`/${story.slug}`}>
                    {get(story, ["sections", 0, "display-name"], "")}
                  </Link>
                </p>
              </div>
              <div styleName="story-meta">
                <div styleName="meta-item">
                  <span styleName="desktop-grid-clock-icon"><ClockIcon /></span>
                  <MobileGridClockIcon />
                  <span>{getPublishTime(story["published-at"] || story["last-published-at"])}</span>
                </div>
                {get(story, ["read-time"]) && (
                  <div styleName="meta-item">
                    <span styleName="desktop-grid-eye-icon"><EyeIcon /></span>
                    <MobileGridEyeIcon />
                    <span>{getReadTime(story)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div styleName="premium-story-block">
      {heroStory && (
        <div styleName="hero-story">
          <div styleName="hero-img-container" style={{ "--object-position": getObjectPosition(get(heroStory, "hero-image-metadata", {})) }}>
            <div styleName="hero-section-tag">
              <Link href={`/${heroStory.slug}`}>
                {get(heroStory, ["sections", 0, "display-name"], "")}
              </Link>
            </div>
            <Link href={`/${heroStory.slug}`} styleName="hero-story-link">
              <div styleName="image-relative-container">
                {getHeroImageSlug(heroStory) ? (
                  <ResponsiveHeroImage
                    story={heroStory}
                    slug={decodeURIComponent(getHeroImageSlug(heroStory))}
                    metadata={get(heroStory, "hero-image-metadata", {})}
                    aspectRatio={[16, 9]}
                    defaultWidth={522}
                    widths={[263, 522]}
                    sizes="(max-width: 1024px) 100vw, 522px"
                    type="image/webp"
                    imgParams={{ auto: ["format", "compress"], fit: "max", q: 70 }}
                    styleName={`hero-img ${get(heroStory, ["hero-image-metadata", "focus-point"]) ? "img-cover" : "img-contain"}`}
                    alt={getHeadline(heroStory) || "Hero image"}
                  />
                ) : (
                  <div styleName="hero-img">
                    <CardImage story={{}} imageType="image16x9" />
                  </div>
                )}
                {heroStory["story-template"] === "photo" && (
                  <div styleName="camera-icon-wrapper">
                    <img src="/icons/imageIcon.png" alt="err" />
                  </div>
                )}
                {heroStory["story-template"] === "video" && (
                  <div styleName="video-icon-wrapper">
                    <VideoIcon />
                  </div>
                )}
              </div>
            </Link>
          </div>
          <div styleName="hero-text">
            <div>
              <Link href={`/${heroStory.slug}`}>
                <h3 styleName="hero-headline">
                  <span styleName="premium-icon-wrapper">
                    <PremiumIcon />
                  </span>
                  {heroStory["story-template"] === "live-blog" && (
                    <span styleName="red-dot-wrapper">
                      <LiveIcon />
                    </span>
                  )}
                  {" "}{getHeadline(heroStory) || ""}
                </h3>
              </Link>
            </div>
            <div styleName="story-meta">
              <div styleName="meta-item">
                <ClockIcon /> <span>{getPublishTime(heroStory["published-at"] || heroStory["last-published-at"])}</span>
              </div>
              {get(heroStory, ["read-time"]) && (
                <div styleName="meta-item">
                  <EyeIcon /> <span>{getReadTime(heroStory)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {gridStories.length > 0 && renderGridRow(gridStories.slice(0, 2))}
      {gridStories.length > 2 && renderGridRow(gridStories.slice(2, 4))}
    </div>
  );
};

PremiumStoryBlock.propTypes = {
  stories: arrayOf(shape({}))
};

export default PremiumStoryBlock;
